import { useState } from 'react';

interface QuoteFormProps {
  title?: string;
  subtitle?: string;
}

const QuoteForm = ({ title = "Get Your Free Quote Today", subtitle = "Fill out the form below and we'll contact you within 24 hours" }: QuoteFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5jbvnhugl990ce152v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 leading-tight">{title}</h3>
      <p className="text-base sm:text-sm text-gray-600 mb-6 leading-relaxed">{subtitle}</p>

      <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="John Smith"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="(479) 337-7565"
            />
          </div>
          <div>
            <label htmlFor="city" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">City *</label>
            <select
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select City</option>
              <option value="Fayetteville">Fayetteville</option>
              <option value="Springdale">Springdale</option>
              <option value="Rogers">Rogers</option>
              <option value="Bentonville">Bentonville</option>
              <option value="Bella Vista">Bella Vista</option>
              <option value="Siloam Springs">Siloam Springs</option>
              <option value="Other">Other NWA Area</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="address" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Street Address</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="123 Main Street"
          />
        </div>

        <div>
          <label htmlFor="service" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Select Service</option>
            <option value="Stone Coated Gutter Cap Installation">Stone Coated Gutter Cap Installation</option>
            <option value="Gutter Guard Installation">Gutter Guard Installation</option>
            <option value="Seamless Gutters">Seamless Gutters</option>
            <option value="Gutter Cleaning">Gutter Cleaning</option>
            <option value="Gutter Repair">Gutter Repair</option>
            <option value="French Drains">French Drains</option>
            <option value="Free Estimate">Free Estimate</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Additional Details</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 sm:py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            placeholder="Tell us about your project..."
          ></textarea>
          <p className="text-sm sm:text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-accent-600 text-white py-4 sm:py-3 rounded-lg text-base sm:text-base font-semibold hover:bg-accent-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap min-h-[56px] touch-manipulation active:scale-95 shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? 'Sending...' : 'Get Free Quote'}
        </button>

        {submitStatus === 'success' && (
          <div className="bg-primary-50 border border-primary-200 text-primary-800 px-4 py-3 rounded-lg text-base sm:text-sm">
            Thank you! We'll contact you within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-base sm:text-sm">
            Something went wrong. Please call us at (479) 337-7565.
          </div>
        )}
      </form>
    </div>
  );
};

export default QuoteForm;
